import React, { useState, useRef, useEffect } from "react";
import { required } from "redux-freeform";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import * as R from "ramda";
import {
  Box,
  Cluster,
  Stack,
  Text,
  ButtonWithAction,
  Dropdown,
  FormInput,
} from "@thecb/components";

import TextInput from "@/components/generic/TextInput";

import { LABEL_PRIMARY } from "@/constants/colors";
import { noop } from "@/util/common";

const DropdownContainer = styled.div`
  width: 250px;

  /* Bootstrap reboot sets line-height: inherit (1.5) on inputs, which grows the dropdown
     to 50px and leaves it 2px taller than the 48px criteria input beside it. A shorter
     line-height puts the content back under the 48px min-height the Dropdown declares. */
  input {
    line-height: 1.25;
  }
`;

const options = [
  { text: "Select search criteria", value: "" },
  { text: "Email address", value: "emailAddress" },
  { text: "First name", value: "firstName" },
  { text: "Last name", value: "lastName" },
];

const CriteriaField = ({
  id,
  fields,
  actions,
  disabledValues,
  criteria,
  updateCriteria,
  descriptionFromKey,
}) => {
  const [value, setValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  const handleClickAway = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickAway);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
    };
  });

  const removeCriteria = (id) => updateCriteria(R.omit([id], criteria));

  const errorMessages = (field) => ({
    [required.error]: `${field} is required`,
  });

  return (
    <Stack direction="row">
      <Box padding="0" maxWidth="36rem">
        <Cluster overflow justify="space-between">
          <Stack direction="row">
            <Stack childGap="4px">
              <Text variant="pS" color={LABEL_PRIMARY} weight="400">
                Search criteria
              </Text>
              <DropdownContainer ref={dropdownRef}>
                <Dropdown
                  placeholder={options[0].text}
                  options={options}
                  disabledValues={disabledValues}
                  value={value}
                  isOpen={isOpen}
                  isError={
                    fields[criteria[id]]?.dirty && fields[criteria[id]]?.hasErrors
                  }
                  onSelect={(value) => {
                    updateCriteria({ ...criteria, [id]: value });
                    setValue(value);
                  }}
                  onClick={() => setIsOpen(!isOpen)}
                />
              </DropdownContainer>
            </Stack>
            <Box padding="0" maxWidth="400px">
              {criteria[id] !== "" ? (
                <FormInput
                  labelTextWhenNoError={descriptionFromKey(criteria[id])}
                  errorMessages={errorMessages(descriptionFromKey(criteria[id]))}
                  field={fields[criteria[id]]}
                  fieldActions={actions[criteria[id]]}
                  showErrors={
                    fields[criteria[id]]?.dirty && fields[criteria[id]]?.hasErrors
                  }
                />
              ) : (
                <Box padding="0" width="240px">
                  <Box padding="12.5px" />
                  <TextInput
                    onBlur={noop}
                    onChange={noop}
                    isError={false}
                    value=""
                    height="48px"
                    data-qa="Criteria input"
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </Cluster>
      </Box>
      {Object.keys(criteria).length > 1 && (
        <Box
          padding="0"
          extraStyles="display: flex; flex: 1; justify-content: flex-end; align-items: center;"
        >
          <ButtonWithAction
            variant="ghost"
            text="Remove this criteria"
            action={() => {
              removeCriteria(id);
              actions[criteria[id]]?.set("");
            }}
            extraStyles="text-decoration: none !important; display: flex;"
            textExtraStyles="font-size: 0.875rem; text-decoration: none !important;"
          />
        </Box>
      )}
    </Stack>
  );
};

const CriteriaForm = ({ fields, actions, descriptionFromKey }) => {
  const [criteria, setCriteria] = useState({ [uuidv4()]: "" });

  const addCriteria = () =>
    setCriteria({
      ...criteria,
      [uuidv4()]: "",
    });
  const criteriaList = Object.keys(criteria);
  return (
    <Box padding="0">
      {criteriaList.map((id, i) => (
        <Box
          key={id}
          padding={`0 0 ${i === criteriaList.length - 1 ? "8px" : "16px"} 0`}
        >
          <CriteriaField
            key={id}
            id={id}
            fields={fields}
            actions={actions.fields}
            disabledValues={Object.values(criteria)}
            criteria={criteria}
            updateCriteria={setCriteria}
            descriptionFromKey={descriptionFromKey}
          />
        </Box>
      ))}
      <ButtonWithAction
        variant="ghost"
        text="Add search criteria"
        action={addCriteria}
        extraStyles="text-decoration: none !important; padding-top: 0;"
        textExtraStyles="font-size: 1rem; font-weight: 400; text-decoration: none !important;"
      />
    </Box>
  );
};

export default CriteriaForm;
